import { useState, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronLeft, Pin, PinOff, Lock, Edit2, Check, X, Trash2, Archive } from 'lucide-react';
import { useForumThread, useCreateForumPost, useEditForumPost, useReactToPost, useVotePoll, useDeleteForumPost, useDeleteForumThread, usePinForumThread } from '@/hooks/useApi';
import { useAuthStore } from '@/store';
import UserAvatar from '@/components/UserAvatar';
import { formatDate } from '@/utils';
import toast from 'react-hot-toast';
import ReportButton from '@/components/ReportButton';
import SEO from '@/components/SEO';

const REACTIONS = [
  { key: 'fire', emoji: '🔥' },
  { key: 'heart', emoji: '❤️' },
  { key: '100', emoji: '💯' },
  { key: 'eyes', emoji: '👀' },
];

// Simple card mention renderer - wraps [[Name]] in a hoverable span
function PostBody({ body }: { body: string }) {
  const parts = body.split(/(\[\[[^\]]+\]\])/g);
  return (
    <div className="prose prose-sm max-w-none text-gray-800 dark:text-zinc-200 whitespace-pre-wrap break-words">
      {parts.map((part, i) => {
        const match = part.match(/^\[\[([^\]]+)\]\]$/);
        if (match) {
          return <CardMentionSpan key={i} cardName={match[1]} />;
        }
        return <span key={i}>{part}</span>;
      })}
    </div>
  );
}

function CardMentionSpan({ cardName }: { cardName: string }) {
  const [show, setShow] = useState(false);
  const [info, setInfo] = useState<any>(null);
  const ref = useRef<HTMLSpanElement>(null);

  const handleHover = async () => {
    setShow(true);
    if (!info) {
      try {
        const { forumService } = await import('@/lib/api');
        const data = await forumService.getCardInfo(cardName);
        setInfo(data);
      } catch {}
    }
  };

  return (
    <span className="relative inline-block">
      <span
        ref={ref}
        className="card-mention text-brand-600 dark:text-brand-400 font-medium underline decoration-dotted cursor-pointer"
        onMouseEnter={handleHover}
        onMouseLeave={() => setShow(false)}
        data-card={cardName}
      >
        {cardName}
      </span>
      {show && (
        <div className="absolute z-50 bottom-full left-0 mb-2 w-64 bg-white dark:bg-zinc-900 rounded-xl shadow-lg dark:shadow-black/40 border border-gray-200 dark:border-zinc-800 p-3 text-left">
          <p className="font-bold text-gray-900 dark:text-zinc-100 text-sm mb-2">{cardName}</p>
          {info ? (
            <div className="space-y-1 text-xs text-gray-600 dark:text-zinc-400">
              <p>Lowest listing: {info.lowest_price ? `$${Number(info.lowest_price).toFixed(2)}` : 'None'}</p>
              <p>Active listings: {info.listing_count ?? 0}</p>
              <p>On {info.wishlist_count ?? 0} wishlists</p>
            </div>
          ) : (
            <p className="text-xs text-gray-500 dark:text-zinc-500">Loading...</p>
          )}
          <div className="flex gap-2 mt-3">
            <Link to={`/explore?q=${encodeURIComponent(cardName)}`}
              className="flex-1 text-center text-xs bg-brand-600 text-white rounded-lg py-1.5 hover:bg-brand-700">
              View listings
            </Link>
          </div>
        </div>
      )}
    </span>
  );
}

function PollSection({ poll, threadId, onVoted }: { poll: any; threadId: string; onVoted: () => void }) {
  const [selected, setSelected] = useState<string>('');
  const { mutate: vote, isLoading } = useVotePoll(threadId);
  const total = poll.options?.reduce((s: number, o: any) => s + (o.vote_count ?? 0), 0) ?? 0;
  const userVoted = poll.user_vote_option_id;

  const handleVote = () => {
    if (!selected) return;
    vote({ pollId: poll.id, optionId: selected }, { onSuccess: onVoted });
  };

  return (
    <div className="mt-4 bg-white dark:bg-zinc-900 rounded-xl p-4 border border-gray-200 dark:border-zinc-800">
      <p className="font-semibold text-gray-800 dark:text-zinc-200 mb-3">{poll.question}</p>
      <div className="space-y-2">
        {poll.options?.map((opt: any) => {
          const pct = total > 0 ? Math.round((opt.vote_count / total) * 100) : 0;
          return (
            <div key={opt.id}>
              {userVoted ? (
                <div className="relative rounded-lg overflow-hidden bg-gray-200 dark:bg-zinc-700 h-9 flex items-center px-3">
                  <div
                    className={`absolute inset-0 ${userVoted === opt.id ? 'bg-brand-200' : 'bg-gray-200 dark:bg-zinc-700'}`}
                    style={{ width: `${pct}%` }}
                  />
                  <span className="relative text-sm font-medium text-gray-800 dark:text-zinc-200 flex-1">{opt.label}</span>
                  <span className="relative text-sm font-bold text-gray-600 dark:text-zinc-400">{pct}%</span>
                </div>
              ) : (
                <label className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-zinc-800 dark:bg-zinc-800">
                  <input
                    type="radio"
                    name={`poll-${poll.id}`}
                    value={opt.id}
                    checked={selected === opt.id}
                    onChange={() => setSelected(opt.id)}
                    className="accent-brand-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-zinc-300">{opt.label}</span>
                </label>
              )}
            </div>
          );
        })}
      </div>
      {!userVoted && (
        <button
          onClick={handleVote}
          disabled={!selected || isLoading}
          className="mt-3 btn-primary text-sm py-1.5 disabled:opacity-50"
        >
          {isLoading ? 'Voting…' : 'Vote'}
        </button>
      )}
      <p className="text-xs text-gray-500 dark:text-zinc-500 mt-2">
        {total} vote{total !== 1 ? 's' : ''}
        {poll.closes_at && ` · Closes ${formatDate(poll.closes_at, 'MMM d, yyyy')}`}
      </p>
    </div>
  );
}

function ReactionBar({ post, threadId }: { post: any; threadId: string }) {
  const { mutate: react } = useReactToPost(threadId);
  const reactions: any[] = post.reactions ?? [];

  return (
    <div className="flex items-center gap-2 mt-3 flex-wrap">
      {REACTIONS.map(({ key, emoji }) => {
        const r = reactions.find((x: any) => x.reaction === key);
        const count = r?.count ?? 0;
        const active = r?.user_reacted ?? false;
        return (
          <button
            key={key}
            onClick={() => react({ postId: post.id, reaction: key })}
            className={`flex items-center gap-1 px-2 py-1 rounded-full text-sm border transition-colors ${
              active
                ? 'bg-brand-50 dark:bg-brand-900/20 border-brand-300 text-brand-700 dark:text-brand-300'
                : 'bg-white dark:bg-zinc-900 border-gray-200 dark:border-zinc-800 text-gray-600 dark:text-zinc-400 hover:bg-gray-100 dark:hover:bg-zinc-800 dark:bg-zinc-800'
            }`}
          >
            {emoji} {count > 0 && <span>{count}</span>}
          </button>
        );
      })}
    </div>
  );
}

export default function ForumThreadPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [replyBody, setReplyBody] = useState('');
  const [editingPostId, setEditingPostId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState('');
  const { user, isAuthenticated } = useAuthStore();

  const { data, isLoading, refetch } = useForumThread(id ?? '', page);
  const { mutate: createPost, isLoading: posting } = useCreateForumPost(id ?? '');
  const { mutate: editPost } = useEditForumPost(id ?? '');
  const { mutate: deletePost } = useDeleteForumPost(id ?? '');
  const { mutate: deleteThread } = useDeleteForumThread();
  const { mutate: pinThread } = usePinForumThread();

  const thread = data?.thread;
  const posts = data?.posts ?? [];
  const totalPages = data?.totalPages ?? 1;
  const poll = data?.poll;

  const handleReply = () => {
    if (!replyBody.trim()) return;
    createPost(replyBody.trim(), {
      onSuccess: () => {
        setReplyBody('');
        setPage(totalPages);
        toast.success('Reply posted!');
      },
    });
  };

  const handleEditSubmit = (postId: string) => {
    editPost({ postId, body: editBody }, {
      onSuccess: () => {
        setEditingPostId(null);
        toast.success('Post updated');
      },
    });
  };

  const handleDeletePost = (postId: string) => {
    if (!window.confirm('Delete this post? This cannot be undone.')) return;
    deletePost(postId, {
      onSuccess: () => toast.success('Post deleted'),
    });
  };

  const handleDeleteThread = () => {
    if (!window.confirm('Delete this entire thread and all its replies? This cannot be undone.')) return;
    deleteThread(id ?? '', {
      onSuccess: () => {
        toast.success('Thread deleted');
        navigate(`/forum/${thread?.category_slug ?? ''}`);
      },
    });
  };

  const handleTogglePin = () => {
    pinThread({ threadId: id ?? '', pinned: !thread?.is_pinned }, {
      onSuccess: () => {
        toast.success(thread?.is_pinned ? 'Thread unpinned' : 'Thread pinned');
        refetch();
      },
    });
  };

  if (isLoading) {
    return (
      <div className="container-custom py-12">
        <div className="skeleton h-12 rounded-xl mb-4" />
        {[...Array(3)].map((_, i) => <div key={i} className="skeleton h-40 rounded-xl mb-4" />)}
      </div>
    );
  }

  if (!thread) {
    return (
      <div className="container-custom py-24 text-center">
        <p className="text-5xl mb-4">🧵</p>
        <h2 className="text-2xl font-bold mb-2">Thread not found</h2>
        <Link to="/forum" className="text-brand-600 dark:text-brand-400 hover:underline">Back to forum</Link>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-zinc-900 min-h-screen">
      <SEO
        title={thread?.title ?? 'Forum Thread'}
        description={`Join the discussion: "${thread?.title ?? 'Forum Thread'}" on Open Card Vault community forum.`}
        url={`/forum/thread/${id}`}
      />
      {/* Breadcrumb + title */}
      <div className="bg-white dark:bg-zinc-900 border-b border-gray-200 dark:border-zinc-800">
        <div className="container-custom py-5">
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-zinc-500 mb-2">
            <Link to="/forum" className="hover:text-brand-600 dark:text-brand-400">Forum</Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <Link to={`/forum/${thread.category_slug}`} className="hover:text-brand-600 dark:text-brand-400">{thread.category_name}</Link>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                {thread.is_pinned && (
                  <span className="flex items-center gap-1 text-xs font-medium text-amber-600 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded-full">
                    <Pin className="h-3 w-3" /> Pinned
                  </span>
                )}
                {thread.is_locked && (
                  <span className="flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-zinc-500 bg-gray-100 dark:bg-zinc-800 px-2 py-0.5 rounded-full">
                    <Lock className="h-3 w-3" /> Locked
                  </span>
                )}
                {thread.archived_at && (
                  <span className="flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-zinc-400 bg-gray-100 dark:bg-zinc-800 px-2 py-0.5 rounded-full">
                    <Archive className="h-3 w-3" /> Archived
                  </span>
                )}
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-zinc-100">{thread.title}</h1>
            </div>
            {/* Admin controls */}
            {user?.is_admin && (
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={handleTogglePin}
                  title={thread.is_pinned ? 'Unpin thread' : 'Pin thread'}
                  className="p-2 rounded-lg text-gray-500 dark:text-zinc-400 hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors"
                >
                  {thread.is_pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
                </button>
                <button
                  onClick={handleDeleteThread}
                  title="Delete thread"
                  className="p-2 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            )}
            {/* Thread author delete (non-admin) */}
            {!user?.is_admin && isAuthenticated && thread.author_id && user?.id === thread.author_id && (
              <button
                onClick={handleDeleteThread}
                title="Delete thread"
                className="p-2 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex-shrink-0"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="container-custom py-6 max-w-4xl">
        {/* Posts */}
        <div className="space-y-4">
          {posts.map((post: any, idx: number) => (
            <div key={post.id} className="bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-5">
              <div className="flex items-start gap-4">
                {post.author_id ? (
                  <Link to={`/profile/${post.author_id}`}>
                    <UserAvatar username={post.author_username ?? '?'} avatarUrl={post.author_avatar} size="md" />
                  </Link>
                ) : (
                  <UserAvatar username="OCV" size="md" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      {post.author_id ? (
                        <Link to={`/profile/${post.author_id}`} className="font-semibold text-gray-900 dark:text-zinc-100 hover:text-brand-600 dark:hover:text-brand-400">
                          {post.author_username}
                        </Link>
                      ) : (
                        <span className="font-semibold text-brand-600 dark:text-brand-400">Open Card Vault</span>
                      )}
                      {post.author_id && post.author_seller_rating > 0 && (
                        <span className="text-xs text-yellow-600">★ {Number(post.author_seller_rating).toFixed(1)}</span>
                      )}
                      {!post.author_id && (
                        <span className="text-xs font-medium text-brand-600 bg-brand-50 dark:bg-brand-900/20 px-1.5 py-0.5 rounded">Staff</span>
                      )}
                      {post.is_edited && (
                        <span className="text-xs text-gray-500 dark:text-zinc-500 italic">(edited)</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 dark:text-zinc-500">{formatDate(post.created_at, 'MMM d, yyyy')}</span>
                      {isAuthenticated && user?.id !== post.author_id && post.author_id && (
                        <ReportButton entityType="forum_post" entityId={post.id} reportedUserId={post.author_id} />
                      )}
                      {isAuthenticated && user?.id === post.author_id && editingPostId !== post.id && (
                        <button
                          onClick={() => { setEditingPostId(post.id); setEditBody(post.body); }}
                          className="p-1 text-gray-500 dark:text-zinc-500 hover:text-brand-600 dark:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-900/20 dark:bg-brand-900/20 rounded transition-colors"
                        >
                          <Edit2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                      {/* Delete button: own post, thread owner deleting a reply, or admin */}
                      {isAuthenticated && post.author_id && editingPostId !== post.id && (
                        user?.id === post.author_id ||
                        user?.is_admin ||
                        (thread?.author_id && user?.id === thread.author_id && idx > 0)
                      ) && (
                        <button
                          onClick={() => handleDeletePost(post.id)}
                          className="p-1 text-gray-400 dark:text-zinc-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                          title="Delete post"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>

                  {editingPostId === post.id ? (
                    <div className="mt-3">
                      <textarea
                        value={editBody}
                        onChange={e => setEditBody(e.target.value)}
                        rows={4}
                        className="w-full border border-gray-200 dark:border-zinc-800 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 resize-none"
                      />
                      <div className="flex gap-2 mt-2">
                        <button onClick={() => handleEditSubmit(post.id)}
                          className="flex items-center gap-1 text-xs bg-brand-600 text-white rounded-lg px-3 py-1.5 hover:bg-brand-700">
                          <Check className="h-3 w-3" /> Save
                        </button>
                        <button onClick={() => setEditingPostId(null)}
                          className="flex items-center gap-1 text-xs text-gray-500 dark:text-zinc-500 hover:text-gray-800 dark:text-zinc-200 px-2 py-1.5">
                          <X className="h-3 w-3" /> Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-2">
                      <PostBody body={post.body} />
                    </div>
                  )}

                  {/* Poll after first post */}
                  {idx === 0 && poll && (
                    <PollSection poll={poll} threadId={id ?? ''} onVoted={() => refetch()} />
                  )}

                  <ReactionBar post={post} threadId={id ?? ''} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="p-2 rounded-lg border border-gray-200 dark:border-zinc-800 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-zinc-800 dark:bg-zinc-800">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm text-gray-600 dark:text-zinc-400">Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="p-2 rounded-lg border border-gray-200 dark:border-zinc-800 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-zinc-800 dark:bg-zinc-800">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Reply box */}
        <div className="mt-6 bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm p-5">
          {thread.archived_at ? (
            <div className="flex items-center gap-2 text-gray-500 dark:text-zinc-400 text-sm">
              <Archive className="h-4 w-4 flex-shrink-0" />
              <span>This thread was archived on {formatDate(thread.archived_at, 'MMM d, yyyy')} after 30 days of inactivity. No new replies can be posted.</span>
            </div>
          ) : thread.is_locked ? (
            <div className="flex items-center gap-2 text-gray-500 dark:text-zinc-500 text-sm">
              <Lock className="h-4 w-4" />
              This thread is locked.
            </div>
          ) : !isAuthenticated ? (
            <p className="text-sm text-gray-500 dark:text-zinc-500">
              <Link to="/login" className="text-brand-600 dark:text-brand-400 font-medium hover:underline">Sign in</Link> to reply.
            </p>
          ) : (
            <>
              <h3 className="font-semibold text-gray-800 dark:text-zinc-200 mb-3">Post a reply</h3>
              <textarea
                value={replyBody}
                onChange={e => setReplyBody(e.target.value)}
                placeholder="Write your reply... Use [[Card Name]] to mention a card."
                rows={4}
                className="w-full border border-gray-200 dark:border-zinc-800 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 resize-none"
              />
              <div className="flex justify-end mt-3">
                <button
                  onClick={handleReply}
                  disabled={!replyBody.trim() || posting}
                  className="btn-primary text-sm py-2 px-6 disabled:opacity-50"
                >
                  {posting ? 'Posting…' : 'Post Reply'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
